import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Header } from './Header';
import { SideMenu } from './SideMenu';
import { Dashboard } from './dashboard/Dashboard';
import { ManagerDashboard } from './dashboard/ManagerDashboard';

import { ModernPickingApp } from './modules/picking/ModernPickingApp';
import { PackingApp } from './modules/packing/PackingApp';
import { ModernPutAwayApp } from './modules/putaway/ModernPutAwayApp';
import { PlaceholderModule } from './modules/PlaceholderModule';
import { ItemAdmin } from './modules/item-admin';
import { Administration, WarehouseMaintenance } from './modules/administration';
import { POrdersReceivingList, POrderReceivingDetail } from './modules/receipt';
import { InventoryApp } from './modules/inventory';
import { WarehouseTransferApp } from './modules/warehouse-transfer';
import { InventoryCorrectionApp } from './modules/inventory-correction';
import { CountingDashboard } from './modules/inventory-counting-dashboard';
import { RackBuilderApp } from './modules/rack-builder';
import { ClaudeQueryApp } from './modules/claude-query';
import { RackBuilderLiveDashboard } from './modules/rack-builder-live';
import { LocationHeatmapApp } from './modules/location-heatmap';
import { ItemLocationBalanceApp } from './modules/item-location-balance';
import { TransactionHistoryApp } from './modules/transaction-history';
import { ReservationsApp } from './modules/reservations';
import { HistoryApp } from './modules/history';
import { UserProfile } from './profile/UserProfile';
import { TestModule } from './modules/testing';
import { ReportConfigurationList } from './modules/administration/ReportConfigurationList';
import { Fase4Test } from './modules/rack-builder-live';
import { NewOrders } from './modules/new-orders';
import { OrderLines } from './modules/order-lines';
import NewPickingLists from './modules/new-picking-lists/NewPickingLists';
import WorkingPickingLists from './modules/new-picking-lists/WorkingPickingLists';
import WorkingOrders from './modules/working-orders/WorkingOrders';
import { CompletedPickingLists } from './modules/history/CompletedPickingLists';
import { OrderHistory } from './modules/order-history';
import { StatisticsModule } from './modules/statistics/StatisticsModule';
import { OrderRegistration } from './modules/order-registration/OrderRegistration';
import { POrderRegistration } from './modules/porder-registration/POrderRegistration';
import { PurchaseOrderLinesList } from './modules/purchase-order-lines';
import { PurchaseOrderHistoryList } from './modules/purchase-order-history';
import { BinPlacementGrunnlagApp } from './modules/bin-placement-grunnlag';
import { WarehouseApp } from './modules/administration/WarehouseApp';
import { SystemRuleMaintenance } from './modules/system-rules';
import { SystemRuleGroupMaintenance } from './modules/system-rule-groups';
import { RuleComposition } from './modules/rule-composition';
import { WarehouseLayoutEditor } from './modules/warehouse-layout';
import { WMSDashboard } from './modules/wms-dashboard';
import { SimulatorMain } from './modules/simulator/SimulatorMain';
import LogViewer from './modules/application-logs/LogViewer';
//import { BarChart3, Truck, ClipboardList, Edit, Move, FileText, History, Settings, User, Shield } from 'lucide-react';
import { BarChart3, Truck, ClipboardList, Edit, Move, FileText, History, Settings, User, Shield, Warehouse } from 'lucide-react';

// Definer module config type
type ModuleId = 'dashboard' | 'manager-dashboard' | 'wms-dashboard' | 'order-registration' | 'porder-registration' | 'new-orders' | 'order-lines' | 'new-picking-lists' | 'working-picking-lists' | 'working-orders' | 'purchase-order-lines' | 'purchase-order-history' | 'completed-picking-lists' | 'order-history' | 'modern-picking' | 'packing' | 'put-away' | 'receipt' | 'receipt-detail' | 'inventory' | 'inventory-counting-dashboard' | 'correction' | 'movement' | 'rack-builder' | 'rack-builder-live' | 'location-heatmap' | 'warehouse-layout-3d' | 'claude-query' | 'item-location-balance' | 'transaction-history' | 'reservations' | 'bin-placement-grunnlag' | 'warehouse-maintenance' | 'warehouse-management' | 'system-rules' | 'system-rule-groups' | 'rule-composition' | 'item-admin' | 'history' | 'reports' | 'report-configuration' | 'statistics' | 'administration' | 'settings' | 'profile' | 'test' | 'fase4-test' | 'simulator' | 'logs';

interface ModuleConfig {
    title: string;
    component: () => React.ReactElement;
}

export const WarehouseMain: React.FC = () => {
    const { user } = useAuth();

    // Bestem default dashboard basert på OperatorGroupId
    const getDefaultDashboard = (): ModuleId => {
        if (!user?.operatorGroupId) return 'dashboard';
        return user.operatorGroupId === '2' ? 'manager-dashboard' : 'dashboard';
    };

    const [currentModule, setCurrentModule] = useState<ModuleId>(getDefaultDashboard());
    const [menuOpen, setMenuOpen] = useState(false);
    const [selectedOrderNo, setSelectedOrderNo] = useState<number | null>(null);
    const [selectedStcOrderNo, setSelectedStcOrderNo] = useState<number | null>(null);

    // Oppdater currentModule når bruker endres (ved ny innlogging)
    useEffect(() => {
        setCurrentModule(getDefaultDashboard());
    }, [user?.operatorGroupId]);

    const handleBackToDashboard = () => {
        handleModuleChange(getDefaultDashboard());
    };

    const moduleConfig: Record<ModuleId, ModuleConfig> = {
        'dashboard': { title: 'Dashboard', component: () => <Dashboard onModuleChange={handleModuleChange} /> },
        'manager-dashboard': { title: 'Lagersjef Dashboard', component: () => <ManagerDashboard onModuleChange={handleModuleChange} /> },
        'wms-dashboard': { title: 'WMS Dashboard', component: () => <WMSDashboard /> },
        'order-registration': { title: 'Ordreregistrering', component: () => <OrderRegistration onBack={handleBackToDashboard} /> },
        'porder-registration': { title: 'Innkjøpsordreregistrering', component: () => <POrderRegistration onBack={handleBackToDashboard} /> },
        'new-orders': { title: 'Nye Ordre', component: () => <NewOrders onBack={handleBackToDashboard} /> },
        'order-lines': { title: 'Ordrelinjer', component: () => <OrderLines onBack={handleBackToDashboard} /> },
        'new-picking-lists': { title: 'Nye Plukklister', component: () => <NewPickingLists onBack={handleBackToDashboard} /> },
        'working-picking-lists': { title: 'Plukklister under arbeid', component: () => <WorkingPickingLists onBack={handleBackToDashboard} /> },
        'working-orders': { title: 'Administrer bestillinger', component: () => <WorkingOrders onBack={handleBackToDashboard} /> },
        'purchase-order-lines': { title: 'Bestillingslinje', component: () => <PurchaseOrderLinesList onBack={handleBackToDashboard} /> },
        'purchase-order-history': { title: 'Bestilling Historikk', component: () => <PurchaseOrderHistoryList onBack={handleBackToDashboard} /> },
        'completed-picking-lists': { title: 'Avsluttede plukklister', component: () => <CompletedPickingLists onBack={handleBackToDashboard} /> },
        'order-history': { title: 'Ordrehistorikk', component: () => <OrderHistory onBack={handleBackToDashboard} /> },
        'modern-picking': { title: 'Plukk', component: () => <ModernPickingApp onBack={handleBackToDashboard} /> },
        'packing': { title: 'Pakking', component: () => <PackingApp onBack={handleBackToDashboard} /> },
        'put-away': { title: 'Plassering', component: () => <ModernPutAwayApp initialStcOrderNo={selectedStcOrderNo || undefined} onBack={handleBackToDashboard} /> },
        'receipt': { title: 'Varemottak', component: () => <POrdersReceivingList onOrderSelect={handleOrderSelect} onBack={handleBackToDashboard} /> },
        'receipt-detail': { title: 'Varemottak - Detalj', component: () => <POrderReceivingDetail orderNo={selectedOrderNo!} onBack={handleBackToList} onNavigateToPutAway={(stcOrderNo) => { setSelectedStcOrderNo(stcOrderNo); setCurrentModule('put-away'); }} /> },
        'inventory': { title: 'Lagertelling', component: () => <InventoryApp onBack={handleBackToDashboard} /> },
        'inventory-counting-dashboard': { title: 'Lagertelling - Oversikt', component: () => <CountingDashboard onBack={handleBackToDashboard} /> },
        'correction': { title: 'Korreksjon', component: () => <InventoryCorrectionApp onBack={handleBackToDashboard} /> },
        'movement': { title: 'Lager-/Lokasjonsflytt', component: () => <WarehouseTransferApp onBack={handleBackToDashboard} /> },
        'rack-builder': { title: 'Reolbygger', component: () => <RackBuilderApp onBack={handleBackToDashboard} /> },
        'rack-builder-live': { title: 'Live Aktivitet', component: () => <RackBuilderLiveDashboard onBack={handleBackToDashboard} /> },
        'location-heatmap': { title: 'Lokasjon HeatMap', component: () => <LocationHeatmapApp onBack={handleBackToDashboard} /> },
        'warehouse-layout-3d': { title: '3D Lagervisning', component: () => <WarehouseLayoutEditor onBack={handleBackToDashboard} /> },
        'claude-query': { title: 'Spør bizClick', component: () => <ClaudeQueryApp onBack={handleBackToDashboard} /> },
        'item-location-balance': { title: 'Vare og lokasjonssaldo', component: () => <ItemLocationBalanceApp onBack={handleBackToDashboard} /> },
        'transaction-history': { title: 'Transaksjonshistorikk', component: () => <TransactionHistoryApp onBack={handleBackToDashboard} /> },
        'reservations': { title: 'Reservasjoner', component: () => <ReservationsApp onBack={handleBackToDashboard} /> },
        'bin-placement-grunnlag': { title: 'Grunnlag - Vareplassering', component: () => <BinPlacementGrunnlagApp onBack={handleBackToDashboard} /> },
        'warehouse-maintenance': { title: 'Vedlikehold Lager', component: () => <WarehouseMaintenance onBack={handleBackToDashboard} /> },
        'warehouse-management': { title: 'Lagersted', component: () => <WarehouseApp onBack={handleBackToDashboard} /> },
        'system-rules': { title: 'Regler', component: () => <SystemRuleMaintenance onBack={handleBackToDashboard} /> },
        'system-rule-groups': { title: 'Regelgrupper', component: () => <SystemRuleGroupMaintenance onBack={handleBackToDashboard} /> },
        'rule-composition': { title: 'Regelsammensetning', component: () => <RuleComposition onBack={handleBackToDashboard} /> },
        'item-admin': { title: 'Vareadministrasjon', component: () => <ItemAdmin onBack={handleBackToDashboard} /> },
        'history': { title: 'Historikk', component: () => <HistoryApp onBack={handleBackToDashboard} /> },
        'reports': { title: 'Rapporter', component: () => <PlaceholderModule icon={BarChart3} title="Rapporter" /> },
        'report-configuration': { title: 'Rapportkonfigurasjon', component: () => <ReportConfigurationList onBack={handleBackToDashboard} /> },
        'statistics': { title: 'Statistikk', component: () => <StatisticsModule onBack={handleBackToDashboard} /> },
        'administration': { title: 'Administrasjon', component: () => <Administration onBack={handleBackToDashboard} /> },
        'test': { title: 'Test og Debugging', component: () => <TestModule /> },
        'fase4-test': { title: 'Fase 4: Live Animasjoner', component: () => <Fase4Test /> },
        'simulator': { title: 'Warehouse Simulator', component: () => <SimulatorMain /> },
        'logs': { title: 'System Logger', component: () => <LogViewer /> },
        'settings': { title: 'Innstillinger', component: () => <PlaceholderModule icon={Settings} title="Innstillinger" /> },
        'profile': { title: 'Brukerprofil', component: () => <UserProfile /> }
    };

    const currentConfig = moduleConfig[currentModule];
    const ModuleComponent = currentConfig.component;

    const handleModuleChange = (moduleId: string) => {
        // Type guard for å sikre at moduleId er gyldig
        if (moduleId in moduleConfig) {
            setCurrentModule(moduleId as ModuleId);
        }
    };

    const handleProfileClick = () => {
        setCurrentModule('profile');
        setMenuOpen(false);
    };

    const handleHomeClick = () => {
        setCurrentModule(getDefaultDashboard());
    };

    const handleOrderSelect = (orderNo: number) => {
        setSelectedOrderNo(orderNo);
        setCurrentModule('receipt-detail');
    };

    const handleBackToList = () => {
        setSelectedOrderNo(null);
        setCurrentModule('receipt');
    };

    return (
        <div className="h-screen bg-gray-50 flex flex-col">
            <Header
                title={currentConfig.title}
                onMenuClick={() => setMenuOpen(true)}
                onProfileClick={handleProfileClick}
                onHomeClick={handleHomeClick}
                onNavigate={handleModuleChange}
            />

            <main className="flex-1 overflow-auto">
                <div className={`mx-auto ${
                    currentModule === 'rack-builder' ||
                    currentModule === 'rack-builder-live' ||
                    currentModule === 'location-heatmap' ||
                    currentModule === 'warehouse-layout-3d' ||
                    currentModule === 'claude-query' ||
                    currentModule === 'item-location-balance' ||
                    currentModule === 'transaction-history' ||
                    currentModule === 'reservations' ||
                    currentModule === 'fase4-test' ||
                    currentModule === 'order-registration' ||
                    currentModule === 'porder-registration' ||
                    currentModule === 'new-orders' ||
                    currentModule === 'order-lines' ||
                    currentModule === 'new-picking-lists' ||
                    currentModule === 'working-picking-lists' ||
                    currentModule === 'working-orders' ||
                    currentModule === 'purchase-order-lines' ||
                    currentModule === 'purchase-order-history' ||
                    currentModule === 'completed-picking-lists' ||
                    currentModule === 'order-history' ||
                    currentModule === 'manager-dashboard' ||
                    currentModule === 'wms-dashboard' ||
                    currentModule === 'warehouse-maintenance' ||
                    currentModule === 'warehouse-management' ||
                    currentModule === 'system-rules' ||
                    currentModule === 'system-rule-groups' ||
                    currentModule === 'rule-composition' ||
                    currentModule === 'inventory-counting-dashboard' ||
                    currentModule === 'report-configuration' ||
                    currentModule === 'simulator'
                    ? 'max-w-none p-0' : 'max-w-7xl p-6'
                }`}>
                    <ModuleComponent />
                </div>
            </main>

            <SideMenu
                isOpen={menuOpen}
                onClose={() => setMenuOpen(false)}
                currentModule={currentModule}
                onModuleChange={handleModuleChange}
            />
        </div>
    );
};
